import { LockFilled } from '@ant-design/icons';
import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { ReadonlyLight } from '@common/general-ui/readonly.component';
import { IAction } from '@common/interfaces/action.interface';
import { css as emotionCSS } from '@emotion/css';
import { SerializedStyles } from '@emotion/react';
import { Checkbox as AntCheckbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

export type ICheckboxChangeHandler = (props: { name: string; checked: boolean }) => void;

export interface ICheckboxProps<TName = string> extends IFieldProps<boolean, TName> {
    className?: string;
    onChange?: ICheckboxChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    readonly?: boolean;
}

export const Checkbox = (props: ICheckboxProps): JSX.Element => {
    const { onChange, onBlur, name } = props;
    const [checked, setChecked] = useState(props.value);

    useEffect(() => {
        setChecked(props.value);
    }, [props.value]);

    const handleChange = useCallback<IAction<CheckboxChangeEvent>>(
        (e) => {
            setChecked(e.target.checked);
            onChange && onChange({ name, checked: e.target.checked });
        },
        [name, onChange],
    );

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        // TODO check typing
        onBlur && onBlur({ ...e, target: { ...e.target, name, value: checked as unknown as string } });
    };

    const handleLabelClick = useCallback(() => {
        setChecked(!checked);
        onChange && onChange({ name: props.name, checked: !checked });
    }, [checked, onChange, props.name]);

    const { className, ...fieldProps } = props;
    return (
        <div css={styles.root} onBlur={handleBlur} className={className} data-testid={`field-${name}`}>
            <div>
                {props.readOnly && <ReadonlyLight />}
                <AntCheckbox
                    tabIndex={0}
                    type="checkbox"
                    css={styles.checkbox(!!props.readOnly)}
                    name={name}
                    checked={checked}
                    value={checked}
                    onChange={handleChange}
                    className={props.readOnly ? 'readonly' : ''}
                />
            </div>
            <div className={styles.labelGroup}>
                <Field
                    {...fieldProps}
                    css={styles.field(!!props.readOnly)}
                    data-testid={`sub-field-${name}`}
                    onLabelClick={handleLabelClick}
                />
                {props.readOnly && <Icon className={styles.icon} icon={LockFilled} tw="text-sm" />}
            </div>
        </div>
    );
};

const styles = {
    root: css`
        position: relative;
        display: flex;
        cursor: pointer;
        outline: none;
    `,
    checkbox: (readOnly: boolean): (SerializedStyles | boolean)[] => [
        css`
            && .ant-checkbox:focus-within {
                outline-color: transparent;
                outline-style: none;
                ${THEME.BOX_SHADOW_GREEN}
            }
        `,
        readOnly &&
            css`
                && .ant-checkbox-checked .ant-checkbox-inner {
                    background-color: ${THEME.GRAY};
                    border-color: ${THEME.GRAY};
                }
            `,
    ],
    field: (readOnly: boolean): (SerializedStyles | boolean)[] => [
        css`
            margin-left: 8px;

            && .field-footer {
                margin-left: -24px;
            }

            && label {
                cursor: pointer;
                padding-bottom: 0px;
                font-weight: 400;
            }
        `,

        readOnly &&
            css`
                label {
                    color: ${THEME.GRAY};
                }
            `,
    ],
    labelGroup: emotionCSS`
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    `,
    icon: emotionCSS`
        margin-left: 8px;
        padding-right: 4px;
    `,
};
